"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileText, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const steps = [{
  number: "01",
  title: "Comprehensive SEO Audit",
  icon: Search,
  description: "I perform a thorough technical and content audit to uncover hidden indexability issues, site speed bottlenecks, and high-value keyword opportunities.",
  deliverables: ["Technical Health Check", "Search Intent & Keyword Gap Analysis", "Prioritised Action Plan"]
}, {
  number: "02",
  title: "On-Page & Content Optimization",
  icon: FileText,
  description: "I refine your page hierarchy, meta tags, search intent alignment, and internal link structure to transform existing copy into high-ranking search assets.",
  deliverables: ["Metadata Overhaul", "Content Structure Optimization", "Internal Link Siloing"]
}, {
  number: "03",
  title: "Local & Authority Citations",
  icon: MapPin,
  description: "I build and clean up high-authority directory listings and NAP citations to strengthen regional trust signals and improve local map pack visibility.",
  deliverables: ["NAP Data Audit & Cleanup", "Niche Citation Building", "Geo-Targeted Signal Enhancements"]
}, {
  number: "04",
  title: "Review & Reputation Strategy",
  icon: Star,
  description: "I help establish structured customer feedback loops to increase authentic positive reviews, boosting conversion rates and user trust signals.",
  deliverables: ["Review Collection Framework", "Trust Signal Integration", "Click-Through Rate Optimization"]
}, {
  number: "05",
  title: "Advanced Schema Markup",
  icon: Code2,
  description: "I deploy customized JSON-LD structured data across key templates to earn rich snippets and establish clear entity understanding for AI search tools.",
  deliverables: ["Custom Schema Implementation", "Entity Disambiguation", "AI Search Engine Readiness"]
}, {
  number: "06",
  title: "Transparent Reporting & Growth",
  icon: BarChart3,
  description: "You receive clean, jargon-free monthly reports demonstrating real organic growth, keyword trajectory, and tangible business impact.",
  deliverables: ["Custom KPI Dashboard", "Direct Monthly Video Review", "Ongoing Strategy Refinements"]
}];
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".step-card", {
        y: 35
      });
      gsap.to(".step-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".connector-line", {
        scaleX: 0,
        transformOrigin: "left center"
      });
      gsap.to(".connector-line", {
        scaleX: 1,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".step-card", {
        opacity: 1,
        y: 0
      });
      gsap.set(".connector-line", {
        scaleX: 1
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="methodologyflow" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 overflow-hidden relative">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-24">
          <Badge variant="secondary" className="mb-4 bg-muted text-foreground border-accent/30 font-medium px-3.5 py-1">
            Proven SEO Framework
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default text-foreground mb-6">
            A Structured Step-by-Step Approach to Organic Growth
          </h2>
          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            When you hire me, you work directly with an experienced SEO consultant. Here is the exact methodology I execute to build sustainable search rankings for your business.
          </p>
        </div>

        <div className="relative">
          <div className="connector-line hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-accent/30 -translate-y-12 z-0" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {steps.map((step, i) => {
            const IconComponent = step.icon;
            return <div key={step.number} data-index={i} className="step-card flex" style={{
              opacity: 0
            }}>
                  <Card className="flex flex-col justify-between w-full border border-border bg-card hover:border-primary/40 transition-all duration-300 shadow-sm hover:shadow-md rounded-xl group relative overflow-hidden">
                    <div className="h-1.5 w-full bg-gradient-to-r from-primary to-accent opacity-80 group-hover:opacity-100 transition-opacity" />

                    <div>
                      <CardHeader className="pt-6 pb-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground font-semibold text-lg flex items-center justify-center shadow-xs font-mono">
                            {step.number}
                          </div>

                          <div className="w-10 h-10 rounded-lg bg-muted text-primary flex items-center justify-center group-hover:bg-secondary/60 transition-colors">
                            <IconComponent className="size-5" />
                          </div>
                        </div>

                        <CardTitle className="text-xl font-bold font-default text-card-foreground group-hover:text-primary transition-colors">
                          {step.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <p className="text-muted-foreground text-sm leading-relaxed font-default">
                          {step.description}
                        </p>

                        <div className="pt-2 border-t border-border/60">
                          <span className="text-xs font-semibold text-foreground tracking-wider uppercase mb-2 block font-default">
                            Key Deliverables:
                          </span>
                          <ul className="space-y-1.5">
                            {step.deliverables.map((item, idx) => <li key={idx} className="text-xs text-muted-foreground flex items-center gap-2">
                                <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                                <span>{item}</span>
                              </li>)}
                          </ul>
                        </div>
                      </CardContent>
                    </div>

                    <div className="px-6 pb-6 pt-2">
                      <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary/60 transition-all duration-500 group-hover:bg-primary" style={{
                      width: `${(i + 1) / steps.length * 100}%`
                    }} />
                      </div>
                    </div>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-16 sm:mt-20 text-center bg-card border border-border rounded-2xl p-8 sm:p-10 shadow-sm max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left">
            <h3 className="text-xl sm:text-2xl font-bold font-default text-card-foreground mb-1">
              Ready to execute a tailored strategy?
            </h3>
            <p className="text-muted-foreground text-sm font-default">
              Book a direct consultation with Yassine Essaadi to discuss your website.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                Book a Free Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent hover:text-accent-foreground font-medium">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}